"use client";

import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";
import { DataChip } from "@/icons/DataTypeIcon";
import { useBoundSourceHighlight } from "@/components/SpotflowV2/Sidebar/useBoundSourceHighlight";
import { useNodeBindings } from "@/components/SpotflowV2/Sidebar/useNodeBindings";

// The engine raises this when a node's bound lists disagree in width. It ships
// the offending refs in `params.widths` rather than baked into the message, so
// this can render them as the same source/output badge the editor uses instead
// of the raw `<uuid>.output` a flow author can't read.
export const ITERATION_WIDTH_MISMATCH = "iteration_width_mismatch";

export function iterationWidths(
  record: Record<string, unknown>
): Record<string, number> | null {
  if (record.identifier !== ITERATION_WIDTH_MISMATCH) return null;
  const params = record.params;
  if (!params || typeof params !== "object" || Array.isArray(params))
    return null;
  const widths = (params as Record<string, unknown>).widths;
  if (!widths || typeof widths !== "object" || Array.isArray(widths))
    return null;
  const out: Record<string, number> = {};
  for (const [ref, n] of Object.entries(widths as Record<string, unknown>)) {
    if (typeof n === "number") out[ref] = n;
  }
  return Object.keys(out).length ? out : null;
}

interface HighlightableChipProps extends ComponentProps<typeof DataChip> {
  refId: string;
}

// Hooks can't run inside the entries loop below, so the hover-highlight
// wiring lives in this per-chip wrapper.
function HighlightableChip({ refId, ...chipProps }: HighlightableChipProps) {
  const { highlight, unhighlight } = useBoundSourceHighlight(refId);
  return (
    <DataChip
      {...chipProps}
      onMouseEnter={highlight}
      onMouseLeave={unhighlight}
    />
  );
}

export function IterationWidthErrorView({
  nodeId,
  widths,
  compact = false,
}: {
  nodeId: string;
  widths: Record<string, number>;
  /** The node card's mini panel — sentence and chips only, no remediation. */
  compact?: boolean;
}) {
  // The offending refs are this node's own bindings, so the run panel's
  // existing resolver already knows their labels, icon and colour — the same
  // ones the config sidebar renders.
  const bindings = useNodeBindings(nodeId);
  const byRef = new Map(bindings.map((b) => [b.refId, b]));

  const entries = Object.entries(widths);

  return (
    <div className={cn(!compact && "space-y-3")}>
      {/* One sentence with the chips inline, so the sizes read as prose
          rather than as decoration on the chip. */}
      <div
        className={cn(
          compact
            ? "text-[10px] leading-6 text-red-600 dark:text-red-400"
            : "text-sm text-foreground leading-7"
        )}
      >
        This module iterates over lists of different lengths:{" "}
        {entries.map(([ref, width], i) => {
          const binding = byRef.get(ref);
          return (
            <span key={ref}>
              <HighlightableChip
                refId={ref}
                // An unresolved ref (node deleted, or a run whose definition
                // isn't loaded) still has to say *something* — fall back to
                // the ref itself rather than rendering an empty chip.
                sourceLabel={binding?.sourceLabel ?? ref}
                sourceImage={binding?.sourceImage}
                sourceIconName={binding?.sourceIconName}
                sourceColor={binding?.sourceColor}
                dataType={binding?.dataType ?? "str"}
                variableLabel={binding?.outputLabel ?? ""}
                isList={binding?.isList ?? false}
                showVariable={!!binding}
                size="sm"
                // text-[10px] alone would inherit the sentence's leading-6
                // and stretch the chip — pin the label line-height down.
                textClassName={compact ? "text-[10px] leading-4" : "text-xs"}
                className="align-middle"
              />{" "}
              <span
                className={cn(compact ? "opacity-80" : "text-muted-foreground")}
              >
                (of size {width})
              </span>
              {i < entries.length - 1 ? ", " : "."}
            </span>
          );
        })}
      </div>
      {!compact && (
        <ol className="list-decimal pl-5 space-y-1 text-sm text-foreground/80">
          <li>
            Make the lists the same length, or reduce one to a single value: a
            single value is reused for every item.
          </li>
          <li>
            If the lists are meant to be independent, split the work into two
            modules.
          </li>
        </ol>
      )}
    </div>
  );
}
